import { Schema } from "effect"
import { AgentId, CommentId, Millis, UserId } from "./ids"

// #region agent-message
/**
 * Decision 0092: one agent's message to another (an ask, the answer to one, or a note that @mentions the agent), as
 * the recipient's chat shows it: a `data-agent-msg` part, rendered like a person's message with the sender's identity.
 * The Project builds it from the comment when it sends the wake (`AgentInbound.Wake.message`); the body stores it in
 * the recipient's chat at once. The comment stays the record (and the event log's actor stays the agent); this is its
 * copy in the chat. The model never reads the part: the turn file carries the same text (decision 0088).
 */
export const AGENT_MESSAGE_PART = "data-agent-msg" as const

/** At most this many characters of a message's text go into the chat (the comment keeps all of it). */
export const AGENT_MESSAGE_MAX = 20_000

export const AgentMessage = Schema.Struct({
  /** The comment this message is. */
  commentId: CommentId,
  /** Decision 0142: `message` is a message in a thread that neither asks nor answers. */
  kind: Schema.Literals(["ask", "answer", "mention", "message"]),
  /**
   * Who wrote it: the agent (its name and @handle), or one of its workers (`worker` = the run's W-n). Decision 0142: or
   * a person (`userId`), through one of their external participants (`via`: "dot", "claude-code").
   */
  from: Schema.Struct({
    agentId: Schema.optionalKey(AgentId),
    userId: Schema.optionalKey(UserId),
    name: Schema.String,
    handle: Schema.String,
    worker: Schema.NullOr(Schema.String),
    via: Schema.optionalKey(Schema.String),
  }),
  /** The agent it was sent to (whose chat shows it). */
  to: AgentId,
  text: Schema.String,
  /** The record it was posted on ("DIC-12" and its title), when it has a ref. */
  on: Schema.NullOr(Schema.Struct({ ref: Schema.String, title: Schema.NullOr(Schema.String) })),
  /** `answer`: the ask it answers. */
  answers: Schema.NullOr(CommentId),
  /** Written by a tainted run (decision 0040): shown as untrusted, never as clean. */
  tainted: Schema.Boolean,
  at: Millis,
  /** Decision 0142: the thread it is in (its id and `T-n`); replies go there with `message{thread}`. */
  thread: Schema.optionalKey(Schema.Struct({ id: CommentId, ref: Schema.String })),
  /** Decision 0142: the sender expects a reply. */
  expectsReply: Schema.optionalKey(Schema.Boolean),
})
export type AgentMessage = typeof AgentMessage.Type

/** The chat message id of an agent message (one per comment, so a redelivered wake stores it once). */
export const agentMessageId = (commentId: string): string => `amsg-${commentId}`
// #endregion agent-message
